import React, { useState, useEffect } from 'react';
import { adminApiClient, Page } from '../lib/api';
import { DataTable } from './common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { FormInput } from './common/FormInput';
import { RichTextEditor } from './common/RichTextEditor';
import {
    FileText,
    Edit2,
    Trash2,
    Plus,
    Globe,
    MoreVertical,
    CheckCircle,
    XCircle,
    Link as LinkIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import { showDeleteConfirmation } from '../lib/sweetAlert';

interface PageFormData {
    title: string;
    slug: string;
    content: string;
    metaTitle: string;
    metaDescription: string;
    isPublished: boolean;
}

const PagesPage: React.FC = () => {
    const [pages, setPages] = useState<Page[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingPage, setEditingPage] = useState<Page | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState<PageFormData>({
        title: '',
        slug: '',
        content: '',
        metaTitle: '',
        metaDescription: '',
        isPublished: true
    });

    useEffect(() => {
        fetchPages();
    }, []);

    const fetchPages = async () => {
        try {
            setLoading(true);
            const response = await adminApiClient.getPages();
            if (response.success && response.data) {
                setPages(response.data.pages);
            } else {
                toast.error(response.message || 'Failed to fetch pages');
            }
        } catch (err) {
            toast.error('Error fetching pages');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingPage(null);
        setFormData({
            title: '',
            slug: '',
            content: '',
            metaTitle: '',
            metaDescription: '',
            isPublished: true
        });
        setShowForm(true);
    };

    const handleEdit = (page: Page) => {
        setEditingPage(page);
        setFormData({
            title: page.title,
            slug: page.slug,
            content: page.content,
            metaTitle: page.metaTitle || '',
            metaDescription: page.metaDescription || '',
            isPublished: page.isPublished
        });
        setShowForm(true);
    };

    const handleDelete = async (id: string, title: string) => {
        const isConfirmed = await showDeleteConfirmation(
            'Delete Page?',
            `Are you sure you want to delete "${title}"? This action cannot be undone.`
        );

        if (!isConfirmed) return;

        try {
            const response = await adminApiClient.deletePage(id);
            if (response.success) {
                toast.success('Page deleted successfully');
                fetchPages();
            } else {
                toast.error(response.message || 'Failed to delete page');
            }
        } catch (err) {
            toast.error('Error deleting page');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title) {
            toast.error('Title is required');
            return;
        }
        if (!formData.slug) {
            toast.error('Slug is required');
            return;
        }

        setIsSubmitting(true);
        const loadingToast = toast.loading(editingPage ? 'Updating page...' : 'Creating page...');

        try {
            let response;
            if (editingPage) {
                response = await adminApiClient.updatePage(editingPage._id, formData);
            } else {
                response = await adminApiClient.createPage(formData);
            }

            if (response.success) {
                toast.success(editingPage ? 'Page updated successfully' : 'Page created successfully', { id: loadingToast });
                fetchPages();
                setShowForm(false);
            } else {
                toast.error(response.message || 'Operation failed', { id: loadingToast });
            }
        } catch (err: any) {
            toast.error(err.message || 'An error occurred', { id: loadingToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    const generateSlug = (title: string) => {
        return title
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleTitleChange = (value: string) => {
        const newSlug = !editingPage && !formData.slug ? generateSlug(value) : formData.slug;
        setFormData({ ...formData, title: value, slug: newSlug === formData.slug && !editingPage ? generateSlug(value) : formData.slug });
    };

    const columns: ColumnDef<Page>[] = [
        {
            header: 'Title',
            accessorKey: 'title',
            cell: (info) => (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-900">{info.getValue() as string}</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <LinkIcon size={10} /> /{info.row.original.slug}
                    </span>
                </div>
            )
        },
        {
            header: 'Status',
            accessorKey: 'isPublished',
            cell: (info) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${info.getValue()
                    ? 'bg-green-50 text-green-700 border border-green-100'
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}>
                    {info.getValue() ? <CheckCircle size={10} className="mr-1" /> : <XCircle size={10} className="mr-1" />}
                    {info.getValue() ? 'Published' : 'Draft'}
                </span>
            )
        },
        {
            header: 'Last Updated',
            accessorKey: 'updatedAt',
            cell: (info) => new Date(info.getValue() as string).toLocaleDateString()
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: (info) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleEdit(info.row.original)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                    >
                        <Edit2 size={16} />
                    </button>
                    <button
                        onClick={() => handleDelete(info.row.original._id, info.row.original.title)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <FileText className="text-orange-600" />
                        Content Managment
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Create and manage dynamic website pages</p>
                </div>
                <button
                    onClick={handleCreate}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 transition-colors"
                >
                    <Plus size={18} className="mr-2" />
                    Create New Page
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <DataTable
                    columns={columns}
                    data={pages}
                    loading={loading}
                    enableClientSidePagination={true}
                />
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200 flex flex-col">
                        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900">
                                {editingPage ? 'Edit Page' : 'Create New Page'}
                            </h3>
                            <button
                                onClick={() => setShowForm(false)}
                                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
                            >
                                <MoreVertical className="transform rotate-90" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6 flex-1 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormInput
                                    label="Page Title"
                                    value={formData.title}
                                    onChange={handleTitleChange}
                                    required
                                    placeholder="e.g. Terms of Service"
                                />

                                <div className="form-group">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Slug (URL Path) <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-mono">/</span>
                                        <input
                                            type="text"
                                            value={formData.slug}
                                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                            className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 sm:text-sm font-mono"
                                            placeholder="terms-of-service"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <RichTextEditor
                                    label="Page Content"
                                    value={formData.content}
                                    onChange={(value) => setFormData({ ...formData, content: value })}
                                    required
                                />
                            </div>

                            <div className="border-t border-gray-100 pt-6">
                                <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Globe size={16} /> SEO Settings
                                </h4>
                                <div className="space-y-4">
                                    <FormInput
                                        label="Meta Title"
                                        value={formData.metaTitle}
                                        onChange={(value) => setFormData({ ...formData, metaTitle: value })}
                                        placeholder="SEO Title (defaults to Page Title if empty)"
                                    />
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                                        <textarea
                                            value={formData.metaDescription}
                                            onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 sm:text-sm"
                                            placeholder="Brief description for search engines..."
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <input
                                    type="checkbox"
                                    id="isPublished"
                                    checked={formData.isPublished}
                                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                />
                                <label htmlFor="isPublished" className="text-sm font-medium text-gray-700 select-none cursor-pointer">
                                    Publish this page immediately
                                </label>
                            </div>

                            <div className="flex gap-4 pt-6 mt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors shadow-sm disabled:opacity-70 flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                                            Saving...
                                        </>
                                    ) : (
                                        editingPage ? 'Update Page' : 'Create Page'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PagesPage;
